import * as React from 'react';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { PinState, PinType, getBusinessPinIcon } from 'utils/mapsUtils';
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles';
import { truncate } from 'es-toolkit/compat';

export interface ConsumerMarkerNewProps {
  onClick(id: number): void;
  isSelected: boolean;
  lat: number;
  lng: number;
  id: number;
  label?: string;
  pinType?: PinType;
}

const useStyles = makeStyles((theme: Theme) => ({
  labelStyle: {
    textShadow: `-1px -1px 0 ${theme.colors.bgDefault},  1px -1px 0 ${theme.colors.bgDefault}, -1px 1px 0 ${theme.colors.bgDefault}, 1px 1px 0 ${theme.colors.bgDefault}`,
  },
}));

/**
 * vis.gl (flag-ON) business marker. Renders the same SVG produced by
 * {@link getBusinessPinIcon} as `<AdvancedMarker>` content. AdvancedMarker's
 * default anchor is bottom-center, matching the legacy `google.maps.Icon` default
 * anchor (no explicit `anchor` set on the business pins). Preserves the hover
 * label, visited state, and z-index behavior of the legacy `ConsumerMarker`.
 */
const ConsumerMarkerNew: React.FunctionComponent<ConsumerMarkerNewProps> = React.memo(
  ({ isSelected, onClick, id, lat, lng, label, pinType }) => {
    const [isMouseOver, setIsMouseOver] = React.useState(false);
    const [hasBeenVisited, setHasBeenVisited] = React.useState(false);
    const styles = useStyles();
    const theme = useTheme();
    const newPinSize = isSelected ? 38 : 32;
    const hasValidCoordinates = Number.isFinite(lat) && Number.isFinite(lng);

    const handleClick = React.useCallback(() => {
      setHasBeenVisited(true);
      onClick(id);
    }, [id, onClick]);

    const getPinState = (): PinState => {
      if (isSelected) {
        return 'selected';
      }
      if (hasBeenVisited) {
        return 'visited';
      }
      return 'enabled';
    };

    const pinState = getPinState();
    const icon = getBusinessPinIcon(newPinSize, pinState, pinType);
    const text = React.useMemo(() => truncate(label || '', { length: 44 }), [label]);
    const showLabel = isMouseOver && !!text;

    const zIndex = isMouseOver ? 9999999 : pinState === 'selected' ? 9999998 : undefined;

    if (!hasValidCoordinates) {
      window.LOGGER?.warn(
        `Skipping invalid map marker coordinates for business ${id}: lat=${lat}, lng=${lng}`
      );
      return null;
    }

    return (
      <AdvancedMarker position={{ lat, lng }} onClick={handleClick} zIndex={zIndex}>
        <div
          onMouseOver={() => setIsMouseOver(true)}
          onMouseOut={() => setIsMouseOver(false)}
          style={{ position: 'relative', width: newPinSize, height: newPinSize }}
        >
          <img src={icon.url} width={newPinSize} height={newPinSize} alt="" />
          {showLabel && (
            <span
              className={styles.labelStyle}
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                marginTop: 6,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: theme.colors.fgDefault,
                fontWeight: theme.typography.fontWeightMedium,
              }}
            >
              {text}
            </span>
          )}
        </div>
      </AdvancedMarker>
    );
  }
);

export default ConsumerMarkerNew;
